import type { Service } from '@typings/content';

/** Copy for the hero's service orbit. */
export const SERVICE_ORBIT = {
  label: 'Our services',
  explore: 'Explore',
} as const;

/**
 * What we do, in the order a client usually needs it.
 *
 * Rendered as cards on the home page (title and summary) and in full on the
 * services page. Edit copy here, never in the components.
 */
export const SERVICES: readonly Service[] = [
  {
    id: 'web',
    shortTitle: 'Web',
    icon: 'code',
    title: 'Web platforms',
    summary: 'Fast, accessible web applications and the back ends that carry them.',
    description:
      'From customer-facing products to internal tools, we build web platforms that load quickly, read well on any screen and stay maintainable long after launch.',
    capabilities: [
      'Product and SaaS applications',
      'Dashboards and internal tools',
      'Headless commerce and content',
      'Performance and accessibility audits',
    ],
    stack: ['React', 'TypeScript', 'Laravel', 'Node.js'],
  },
  {
    id: 'mobile',
    shortTitle: 'Mobile',
    icon: 'phone',
    title: 'Mobile apps',
    summary: 'Native-feeling apps for iOS and Android from a single codebase.',
    description:
      'We design and ship mobile apps that feel at home on every device, with offline support, push notifications and the release pipeline to keep them current.',
    capabilities: [
      'Cross-platform apps with Flutter',
      'Native Android development',
      'Offline-first data sync',
      'App Store and Play Store delivery',
    ],
    stack: ['Flutter', 'Dart', 'Kotlin', 'Firebase'],
  },
  {
    id: 'ai',
    shortTitle: 'AI',
    icon: 'spark',
    title: 'Artificial intelligence',
    summary: 'Models that do the work, not the talking.',
    description:
      'Language models, automation and decision systems wired into the products people already use, evaluated against real outcomes rather than demos.',
    capabilities: [
      'LLM-powered features and assistants',
      'Retrieval over your own documents',
      'Workflow and back-office automation',
      'Evaluation and safety guardrails',
    ],
    stack: ['Python', 'LLM APIs', 'Vector search', 'MLOps'],
  },
  {
    id: 'cloud',
    shortTitle: 'Cloud',
    icon: 'cloud',
    title: 'Cloud & DevOps',
    summary: 'Systems that come up and stay up.',
    description:
      'Cloud architecture, delivery pipelines and observability built to carry real load, so releases are routine and incidents are rare and short.',
    capabilities: [
      'Cloud architecture and migration',
      'CI/CD and infrastructure as code',
      'Monitoring, logging and alerting',
      'Cost and reliability reviews',
    ],
    stack: ['AWS', 'Docker', 'Kubernetes', 'Terraform'],
  },
  {
    id: 'desktop',
    shortTitle: 'Enterprise',
    icon: 'window',
    title: 'Desktop & enterprise',
    summary: 'Robust software for the systems a business runs on.',
    description:
      'Line-of-business applications, integrations with legacy systems and the careful modernisation of software that cannot afford downtime.',
    capabilities: [
      'Windows desktop applications',
      'Enterprise system modernisation',
      'Reporting and data tooling',
      'Long-term maintenance',
    ],
    stack: ['.NET', 'C#', 'SQL Server', 'Azure'],
  },
  {
    id: 'apis',
    shortTitle: 'APIs',
    icon: 'link',
    title: 'APIs & integration',
    summary: 'Clean interfaces between every system you depend on.',
    description:
      'Well-documented APIs and dependable integrations that connect payments, CRMs, ERPs and partners without brittle glue code.',
    capabilities: [
      'REST and GraphQL API design',
      'Third-party and payment integrations',
      'Event-driven architecture',
      'Documentation and SDKs',
    ],
    stack: ['OpenAPI', 'GraphQL', 'Webhooks', 'Queues'],
  },
];
